/**
 * Shared PowerPoint (.pptx) exporter for TraceElements pitch vaults.
 * Each vault flattens its own SLIDES array into a normalized shape and
 * calls exportPitchPptx() to trigger a client-side .pptx download.
 *
 * Design:
 *  - Widescreen 16:9 layout (13.33 × 7.5 in)
 *  - Dark slate background matching the vault aesthetic
 *  - Cover slide + one slide per pitch slide
 *  - Kicker (mono) + title (bold) + body + bullets
 */

import pptxgen from "pptxgenjs";

// Normalize any hex like "#FF3355", "FF3355", or "ff3355" → "FF3355" for pptxgenjs.
const hex = (c) => (c || "F5F5F7").toString().replace(/^#/, "").toUpperCase().padEnd(6, "0").slice(0, 6);

// Strip HTML tags + collapse whitespace — vault titles use inline <span> for accent.
const clean = (s) =>
  (s || "")
    .replace(/<[^>]+>/g, "")
    .replace(/&ldquo;|&rdquo;/g, '"')
    .replace(/&mdash;/g, "—")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();

/**
 * @param {Object} opts
 * @param {string} opts.vaultName     — used in filename + slide footer
 * @param {string} opts.title         — cover title
 * @param {string} opts.subtitle      — cover subtitle (usually the vault tagline)
 * @param {string} opts.brand         — primary accent hex (e.g. "B91C2C")
 * @param {string} [opts.accent]      — secondary accent hex (defaults to brand)
 * @param {string} [opts.background]  — background hex (default dark slate)
 * @param {Array}  opts.slides        — [{ kicker, title, body, bullets: string[] }]
 * @param {string} [opts.footer]      — bottom-strip text (default "CONFIDENTIAL")
 */
/**
 * Generic flattener: walks any known content field on a vault slide and
 * produces a flat list of bullet strings for the PPT slide.
 * Each vault uses different content shapes; this covers all of them.
 */
export function flattenSlideForPptx(slide) {
  const bullets = [];
  const push = (s) => { if (s) bullets.push(clean(s)); };
  const s = slide;

  // 1: direct bullets
  if (Array.isArray(s.bullets)) s.bullets.forEach(push);

  // 2: problems / solution / moat / features / recycling / dual / personas / hardware
  const objLists = [
    ["problems",  (o) => `${o.tag ? o.tag + " — " : ""}${o.title}: ${o.desc}`],
    ["solution",  (o) => `${o.title}: ${o.desc}`],
    ["features",  (o) => `${o.title}: ${o.desc}`],
    ["recycling", (o) => `${o.phase} · ${o.title}: ${o.desc}`],
    ["dual",      (o) => `${o.tag} · ${o.title}: ${(o.items || []).join(" · ")}`],
    ["personas",  (o) => `${o.tag} · ${o.title}: ${o.desc}${o.size ? " (" + o.size + ")" : ""}`],
    ["hardware",  (o) => `${o.label}: ${o.tech} — ${o.spec}`],
    ["moat",      (o) => `${o.title}: ${o.desc}`],
    ["steps",     (o) => `Step ${o.n} · ${o.label}: ${o.desc}`],
    ["onboarding",(o) => `Step ${o.n} · ${o.label}: ${o.desc}`],
    ["pillars5",  (o) => `${o.tag} · ${o.title}: ${o.desc}`],
    ["tiers",     (o) => `${o.name} (${o.tag}) · ${o.price} — ${o.note}`],
    ["panda",     (str) => str],
  ];
  objLists.forEach(([key, fn]) => {
    if (Array.isArray(s[key])) s[key].forEach((o) => push(typeof o === "string" ? o : fn(o)));
  });

  // 3: pillars (dual-engine with capabilities)
  if (Array.isArray(s.pillars)) {
    s.pillars.forEach((p) => {
      push(`${p.tag ? p.tag + " · " : ""}${p.title}`);
      (p.capabilities || []).forEach((c) => push(`  · ${c}`));
    });
  }

  // 4: matrix table
  if (Array.isArray(s.matrix)) {
    s.matrix.forEach((r) => push(`${r.service} — Labor ${r.labor} / Parts ${r.parts} → Total ${r.total}`));
  }

  // 5: unit economics walkthrough
  if (s.walkthrough) {
    const w = s.walkthrough;
    (w.flow || []).forEach((f) => push(`Step ${f.step} · ${f.label}: ${f.value} — ${f.detail}`));
    (w.costs || []).forEach(([k, v]) => push(`${k}: ${v}`));
    (w.income || []).forEach(([k, v]) => push(`${k}: ${v}`));
    (w.kpis || []).forEach((k) => push(`${k.label}: ${k.value}`));
  }

  // 6: pilot financial model
  if (s.pilot) {
    const p = s.pilot;
    [...(p.inflows || []), ...(p.outflows || []), ...(p.net || [])].forEach(([k, v]) => push(`${k}: ${v}`));
    (p.kpis || []).forEach((k) => push(`${k.label}: ${k.value}`));
  }

  // 7: phases (call-to-action)
  if (Array.isArray(s.phases)) {
    s.phases.forEach((p) => {
      push(`${p.week} · ${p.title}`);
      (p.items || []).forEach((it) => push(`  · ${it}`));
    });
  }

  // 8: roadmap (Endexia)
  if (Array.isArray(s.roadmap)) {
    s.roadmap.forEach((q) => {
      const status = q.status ? ` [${q.status.toUpperCase()}]` : "";
      push(`${q.quarter}${status} · ${q.title}`);
      (q.items || []).forEach((it) => push(`  · ${it}`));
    });
  }

  // 9: the ask
  if (s.ask) {
    const a = s.ask;
    push(`${a.round}: ${a.raise}`);
    (a.allocation || []).forEach((al) => push(`${al.pct}% · ${al.label}`));
    (a.cta || []).forEach((c) => push(c.label));
  }

  // 10: KPI dashboard + AI flow (STILL slide 4)
  if (Array.isArray(s.kpis) && !s.walkthrough && !s.pilot) {
    s.kpis.forEach((k) => push(k.label));
  }
  if (s.aiFlow) {
    push(`${s.aiFlow.title}: ${s.aiFlow.subtitle}`);
    if (Array.isArray(s.aiFlow.steps)) push(`Flow: ${s.aiFlow.steps.join(" → ")}`);
    if (s.aiFlow.note) push(s.aiFlow.note);
  }

  // 11: medication vault (STILL slide 5)
  if (s.med) {
    (s.med.vault || []).forEach(push);
    if (s.med.ocr) {
      push(`${s.med.ocr.title}: ${(s.med.ocr.steps || []).join(" → ")}`);
      if (s.med.ocr.note) push(s.med.ocr.note);
    }
    if (s.med.glp) {
      push(s.med.glp.title);
      (s.med.glp.features || []).forEach((f) => push(`  · ${f}`));
    }
  }

  // 12: fitness selectors + AI workout (STILL slide 6)
  if (s.fitness) {
    (s.fitness.selectors || []).forEach((sel) => push(`${sel.title}: ${(sel.options || []).join(" · ")}`));
    if (s.fitness.ai) push(`${s.fitness.ai.title} · ${s.fitness.ai.engine}: ${s.fitness.ai.desc}`);
  }

  // 13: brain + mobile (STILL slide 8)
  if (s.brainMobile) {
    const b = s.brainMobile;
    (b.brain || []).forEach(push);
    (b.questions || []).forEach((q) => push(`Ask: "${q}"`));
    (b.mobile || []).forEach((m) => push(m.label));
  }

  // 14: gamification (STILL slide 9)
  if (s.gamification) {
    (s.gamification.systems || []).forEach((sys) => push(`${sys.tag} · ${sys.title}: ${sys.desc}`));
    (s.gamification.badges || []).forEach((b) => push(`Badge · ${b.name}: ${b.desc}`));
  }

  // 15: closing (STILL slide 10)
  if (s.closing) {
    const c = s.closing;
    if (Array.isArray(c.mantras)) push(`Mantras: ${c.mantras.join("  ·  ")}`);
    (c.builtFor || []).forEach((b) => push(`Built for: ${b}`));
    if (c.signoff) push(c.signoff);
    if (c.tagline) push(c.tagline);
  }

  return bullets;
}

export function exportPitchPptx(opts) {
  const {
    vaultName,
    title,
    subtitle = "",
    brand = "FF3355",
    accent,
    background = "0B0C10",
    slides = [],
    footer = "CONFIDENTIAL · Prepared by TraceElements",
  } = opts;

  const brandHex = hex(brand);
  const accentHex = hex(accent || brand);
  const bgHex = hex(background);
  const dim = "9CA3AF";
  const light = "F5F5F7";

  const pres = new pptxgen();
  pres.author = "TraceElements";
  pres.company = "TraceElements Technology Group";
  pres.title = vaultName;
  pres.subject = subtitle;
  pres.layout = "LAYOUT_WIDE"; // 13.33 × 7.5 in

  const W = 13.333;

  // ============ COVER SLIDE ============
  const cover = pres.addSlide();
  cover.background = { color: bgHex };

  // Left accent bar
  cover.addShape(pres.ShapeType.rect, {
    x: 0, y: 0, w: 0.35, h: 7.5, fill: { color: brandHex }, line: { color: brandHex },
  });
  // Kicker
  cover.addText(`// ${vaultName.toUpperCase()} · PITCH VAULT`, {
    x: 0.75, y: 0.7, w: W - 1.5, h: 0.4,
    fontSize: 12, fontFace: "Consolas", color: brandHex, bold: true, charSpacing: 4,
  });
  // Big title
  cover.addText(title, {
    x: 0.75, y: 1.35, w: W - 1.5, h: 3.2,
    fontSize: 60, fontFace: "Calibri", color: light, bold: true, valign: "top",
  });
  // Subtitle
  if (subtitle) {
    cover.addText(subtitle, {
      x: 0.75, y: 4.7, w: W - 1.5, h: 1.6,
      fontSize: 20, fontFace: "Calibri", color: dim, valign: "top",
    });
  }
  // Footer
  cover.addText(footer, {
    x: 0.75, y: 6.9, w: W - 1.5, h: 0.35,
    fontSize: 10, fontFace: "Consolas", color: dim, charSpacing: 3,
  });

  // ============ CONTENT SLIDES ============
  slides.forEach((s, idx) => {
    const sl = pres.addSlide();
    sl.background = { color: bgHex };
    sl.addShape(pres.ShapeType.rect, {
      x: 0, y: 0, w: 0.2, h: 7.5, fill: { color: brandHex }, line: { color: brandHex },
    });

    // Kicker
    if (s.kicker) {
      sl.addText(clean(s.kicker), {
        x: 0.55, y: 0.5, w: W - 1.1, h: 0.35,
        fontSize: 11, fontFace: "Consolas", color: brandHex, bold: true, charSpacing: 3,
      });
    }
    // Title
    sl.addText(clean(s.title), {
      x: 0.55, y: 0.95, w: W - 1.1, h: 1.4,
      fontSize: 38, fontFace: "Calibri", color: light, bold: true, valign: "top",
    });
    // Body (paragraph)
    let cursorY = 2.6;
    if (s.body) {
      sl.addText(clean(s.body), {
        x: 0.55, y: cursorY, w: W - 1.1, h: 1.2,
        fontSize: 14, fontFace: "Calibri", color: dim, valign: "top",
      });
      cursorY += 1.4;
    }
    // Bullets
    const bullets = (s.bullets || []).filter(Boolean).map((b) => clean(b));
    if (bullets.length > 0) {
      // Two-column layout if many bullets to keep slide tidy
      const useTwoCol = bullets.length >= 6;
      if (useTwoCol) {
        const mid = Math.ceil(bullets.length / 2);
        const left = bullets.slice(0, mid);
        const right = bullets.slice(mid);
        const colH = 7.5 - cursorY - 0.7;
        sl.addText(left.map((t) => ({ text: t, options: { bullet: { code: "25A0" }, color: light } })), {
          x: 0.55, y: cursorY, w: (W - 1.1) / 2 - 0.15, h: colH,
          fontSize: 13, fontFace: "Calibri", paraSpaceAfter: 8, valign: "top",
        });
        sl.addText(right.map((t) => ({ text: t, options: { bullet: { code: "25A0" }, color: light } })), {
          x: 0.55 + (W - 1.1) / 2 + 0.15, y: cursorY, w: (W - 1.1) / 2 - 0.15, h: colH,
          fontSize: 13, fontFace: "Calibri", paraSpaceAfter: 8, valign: "top",
        });
      } else {
        sl.addText(bullets.map((t) => ({ text: t, options: { bullet: { code: "25A0" }, color: light } })), {
          x: 0.55, y: cursorY, w: W - 1.1, h: 7.5 - cursorY - 0.7,
          fontSize: 15, fontFace: "Calibri", paraSpaceAfter: 10, valign: "top",
        });
      }
    }

    // Footer strip: slide number + confidential label
    sl.addShape(pres.ShapeType.rect, {
      x: 0, y: 7.15, w: W, h: 0.02, fill: { color: accentHex }, line: { color: accentHex },
    });
    sl.addText(`${vaultName.toUpperCase()} · ${String(idx + 1).padStart(2, "0")} / ${String(slides.length).padStart(2, "0")}`, {
      x: 0.55, y: 7.2, w: W - 1.1, h: 0.28,
      fontSize: 9, fontFace: "Consolas", color: dim, charSpacing: 2,
    });
    sl.addText("CONFIDENTIAL · TraceElements", {
      x: 0.55, y: 7.2, w: W - 1.1, h: 0.28,
      fontSize: 9, fontFace: "Consolas", color: dim, charSpacing: 2, align: "right",
    });
  });

  // Filename: "Advantage-Fleet-Pitch-Vault-2026.pptx"
  const safe = vaultName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const year = new Date().getFullYear();
  return pres.writeFile({ fileName: `${safe}-Pitch-Vault-${year}.pptx` });
}
